/* eslint-disable react-hooks/exhaustive-deps */
import { Button, Card, Checkbox } from '@features/ui';
import { useGetMe } from '@src/hooks/useGetMe';
import { MarketDataItem } from '@src/types/marketData';
import axios from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export const MarketDataPage = () => {
  const navigate = useNavigate();
  const { me } = useGetMe();
  const [marketData, setMarketData] = useState<MarketDataItem[]>([]);
  const [selectedMarket, setSelectedMarket] = useState<MarketDataItem | null>(
    null
  );

  const handleCheckboxChange = useCallback((market: MarketDataItem) => {
    setSelectedMarket(market);
  }, []);

  const handleCheckout = useCallback(() => {
    if (!selectedMarket) {
      return;
    }
    navigate('/market-data/checkout', {
      state: { selectedMarket },
    });
  }, [selectedMarket, navigate]);

  const handleFetchMarketData = useCallback(async () => {
    try {
      const res = await axios.get('/products/markets-data/', {
        params: {
          datafeed: me?.platform,
        },
      });

      setMarketData(res.data);
    } catch (error) {
      console.error(error);
    }
  }, [me?.platform]);

  useEffect(() => {
    handleFetchMarketData().then(() => null);
  }, [me?.platform]);

  return (
    <div className="min-h-screen pb-[120px]">
      <div className="max-w-[1440px] m-auto px-6 lg:px-[60px] mt-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">
            Level 2 Market Data
          </h1>
          <p className="text-white/70 text-base">
            Select market data subscriptions to add to your account
          </p>
        </div>

        <Card className="bg-[#1F2734] p-6">
          <div className="space-y-4">
            {/* Header Row */}
            <div className="grid grid-cols-[2fr_1fr_80px] gap-4 pb-4 border-b border-[#33455F]">
              <div className="text-sm font-semibold text-[#818FA4] uppercase">
                Market Name
              </div>
              <div className="text-sm font-semibold text-[#818FA4] uppercase">
                Price
              </div>
              <div className="text-sm font-semibold text-[#818FA4] uppercase text-center">
                Select
              </div>
            </div>

            {/* Market Data Rows */}
            {marketData.map((market) => (
              <div
                key={market.id}
                className="grid grid-cols-[2fr_1fr_80px] gap-4 items-center py-4 border-b border-[#33455F]/50 hover:bg-[#253348]/30 transition-colors rounded-lg px-2"
              >
                <div className="space-y-1">
                  <p className="text-white font-medium text-base">
                    {market.display_name_web}
                  </p>
                </div>
                <div className="text-white font-semibold text-lg">
                  ${market.billing_plan.first_price.toFixed(2)}
                </div>
                <div className="flex justify-center">
                  <Checkbox
                    checked={selectedMarket?.id === market.id}
                    className="w-[22px] h-[22px] border-2 border-white"
                    id={`market-${market.id}`}
                    onCheckedChange={() => handleCheckboxChange(market)}
                  />
                </div>
              </div>
            ))}

            {marketData.length === 0 && (
              <p className="text-white text-center py-6">No market data.</p>
            )}
          </div>

          {/* Summary Section */}
          {selectedMarket && (
            <div className="mt-6 pt-6 border-t border-[#33455F]">
              <div className="flex justify-between items-center mb-4">
                <span className="text-white font-semibold text-lg">
                  Selected: {selectedMarket.display_name_web}{' '}
                </span>
                <span className="text-white font-bold text-xl">
                  Total: ${selectedMarket.billing_plan.first_price.toFixed(2)}
                </span>
              </div>
              <Button
                className="w-full text-base font-bold"
                size="lg"
                variant="gradient"
                onClick={handleCheckout}
              >
                Proceed to Checkout
              </Button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};
